import { isDbtFailedStatus } from "./dbtCauses";

/**
 * Round 6, T5: the transform step's model counts, one rule for the runner and every screen.
 *
 * "Built" is the models that SUCCEEDED. Before this the runner counted only failures
 * (`modelsFailed`) and every reader computed built as reported minus failed, so a model dbt
 * skipped because a model it depends on failed counted as built: the dev run whose two sources
 * were missing read "2 of 4 built" while nothing was built. The poll step now writes four counts
 * that add up to `modelsReported` (`modelsSucceeded`, `modelsFailed`, `modelsSkipped`,
 * `modelsNotRun`); a row written before them keeps today's arithmetic (`exact: false`).
 *
 * Counts only, never a node name or a message (SEC-17).
 */

export type DbtModelOutcome = "succeeded" | "failed" | "skipped" | "not_run";

/** dbt statuses of a node that did what it was asked. */
const SUCCEEDED: ReadonlySet<string> = new Set(["success", "pass", "no-op", "warn"]);

/** The outcome a (constrained) dbt status counts under; anything unrecognised is `not_run`. */
export function dbtModelOutcome(status: string): DbtModelOutcome {
  if (isDbtFailedStatus(status)) return "failed";
  if (status === "skipped") return "skipped";
  return SUCCEEDED.has(status) ? "succeeded" : "not_run";
}

/** The four step metrics, from the statuses of the nodes dbt reported. */
export interface DbtModelTally {
  modelsSucceeded: number;
  modelsFailed: number;
  modelsSkipped: number;
  modelsNotRun: number;
}

export function tallyDbtModels(statuses: readonly string[]): DbtModelTally {
  const tally: DbtModelTally = {
    modelsSucceeded: 0,
    modelsFailed: 0,
    modelsSkipped: 0,
    modelsNotRun: 0,
  };
  for (const status of statuses) {
    const outcome = dbtModelOutcome(status);
    if (outcome === "succeeded") tally.modelsSucceeded += 1;
    else if (outcome === "failed") tally.modelsFailed += 1;
    else if (outcome === "skipped") tally.modelsSkipped += 1;
    else tally.modelsNotRun += 1;
  }
  return tally;
}

/** What a reader has of a step's counts: the two old keys always, the new three when written. */
export interface DbtModelCountsInput {
  modelsReported: number | null;
  modelsFailed: number | null;
  modelsSucceeded?: number | null;
  modelsSkipped?: number | null;
  modelsNotRun?: number | null;
}

export interface DbtModelCounts {
  /** The nodes the run selected and dbt reported on (`modelsReported`). */
  selected: number;
  built: number;
  failed: number;
  skipped: number;
  /** Selected nodes dbt reported in a state that is none of the three above. */
  notRun: number;
  /** False for a row written before the four counts: built is reported minus failed. */
  exact: boolean;
}

function count(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/** The counts to show, or null when dbt reported no node at all. */
export function dbtModelCountsOf(row: DbtModelCountsInput): DbtModelCounts | null {
  const selected = count(row.modelsReported);
  if (selected === null) return null;
  const failed = count(row.modelsFailed) ?? 0;
  const succeeded = count(row.modelsSucceeded);
  if (succeeded === null) {
    return {
      selected,
      built: Math.max(0, selected - failed),
      failed,
      skipped: 0,
      notRun: 0,
      exact: false,
    };
  }
  return {
    selected,
    built: succeeded,
    failed,
    skipped: count(row.modelsSkipped) ?? 0,
    notRun: count(row.modelsNotRun) ?? 0,
    exact: true,
  };
}

/** "0 of 4 built: 2 failed, 2 skipped because a model they depend on failed". */
export function dbtModelCountsSentence(counts: DbtModelCounts): string {
  const head = `${counts.built} of ${counts.selected} built`;
  const parts: string[] = [];
  if (counts.failed > 0) parts.push(`${counts.failed} failed`);
  if (counts.skipped > 0) {
    parts.push(
      `${counts.skipped} skipped because a model ${counts.skipped === 1 ? "it depends" : "they depend"} on failed`,
    );
  }
  if (counts.notRun > 0) parts.push(`${counts.notRun} not run`);
  return parts.length > 0 ? `${head}: ${parts.join(", ")}` : head;
}
